import { CartProduct } from '@/contexts/CartContext';
import { clearCartStorage } from '@/utils/cart/cartStorageUtils';
import { saveAppliedCreditToStorage } from '@/utils/cart/cartStorageUtils';
import { ttAddToCart } from '@/lib/tiktokPixel';


// Debounce mechanism to prevent duplicate cart additions
let addToCartTimeout: NodeJS.Timeout | null = null;

export const useCartActions = (
  userId: string,
  isAuthenticated: boolean,
  setCartItems: (items: CartProduct[] | ((prev: CartProduct[]) => CartProduct[])) => void,
  setPromoCode: (code: string) => void,
  setAppliedCredit: (credit: number) => void,
  supabaseOperations: {
    addToCartSupabase: any;
    removeFromCartSupabase: any;
    updateCartItemQuantitySupabase: any;
    clearCartSupabase: any;
  }
) => {
  const addToCart = async (product: Omit<CartProduct, 'quantity'>) => {
    // Clear any existing timeout
    if (addToCartTimeout) {
      clearTimeout(addToCartTimeout);
    }

    // Debounce the add to cart action
    addToCartTimeout = setTimeout(async () => {
      console.log('Adding product to cart:', product.id);

      ttAddToCart({
        content_id: String(product.id),
        content_name: product.name,
        price: product.price,
      });
      setCartItems(prevItems => {

        const existingItemIndex = prevItems.findIndex(item => item.id === product.id);

        if (existingItemIndex > -1) {
          const newItems = [...prevItems];
          newItems[existingItemIndex].quantity += 1;
          
          // Sync with Supabase if authenticated
          if (isAuthenticated) {
            supabaseOperations.updateCartItemQuantitySupabase(
              userId, 
              product.id, 
              newItems[existingItemIndex].quantity
            ).catch((err: any) => console.error('Error updating cart item quantity in Supabase:', err));
          }
          
          return newItems;
        } else {
          const newItem = { ...product, quantity: 1 };
          
          // Sync with Supabase if authenticated
          if (isAuthenticated) {
            supabaseOperations.addToCartSupabase(userId, {
              product_id: product.id,
              name: product.name,
              price: product.price,
              image: product.image,
              quantity: 1
            }).catch((err: any) => console.error('Error adding item to Supabase cart:', err));
          }
          
          return [...prevItems, newItem];
        }
      });
    }, 300); // 300ms debounce
  };

  const removeFromCart = (productId: number) => {
    setCartItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== productId);
      
      // Sync with Supabase if authenticated
      if (isAuthenticated) {
        supabaseOperations.removeFromCartSupabase(userId, productId)
          .catch((err: any) => console.error('Error removing item from Supabase cart:', err));
      }
      
      return newItems;
    });
  };

  const updateQuantity = (productId: number, quantity: number) => {
    setCartItems(prevItems => {
      const newItems = prevItems.map(item => {
        if (item.id === productId) {
          // Sync with Supabase if authenticated and quantity is at least 1
          if (isAuthenticated && quantity >= 1) {
            supabaseOperations.updateCartItemQuantitySupabase(userId, productId, quantity)
              .catch((err: any) => console.error('Error updating quantity in Supabase cart:', err));
          } else if (isAuthenticated && quantity < 1) {
            supabaseOperations.removeFromCartSupabase(userId, productId)
              .catch((err: any) => console.error('Error removing item from Supabase cart:', err));
          }
          
          return { ...item, quantity: quantity };
        }
        return item;
      }).filter(item => item.quantity > 0); // Remove items with quantity < 1
      
      return newItems;
    });
  };

  const clearCart = async () => {
    setCartItems([]);
    setPromoCode('');
    setAppliedCredit(0);
    
    // Clear both local storage and Supabase
    clearCartStorage(userId);
    
    if (isAuthenticated) {
      try {
        await supabaseOperations.clearCartSupabase(userId);
        console.log('Cart cleared in Supabase');
      } catch (error) {
        console.error('Error clearing cart in Supabase:', error);
      }
    }
  };
  
  // Function to apply store credit
  const applyCredit = (amount: number) => {
    console.log(`Applying ${amount} store credit`);
    setAppliedCredit(amount);
    
    // Save to local storage to persist across refreshes
    saveAppliedCreditToStorage(userId, amount);
  };
  
  // Function to reset applied credit
  const resetAppliedCredit = () => {
    console.log('Resetting applied credit to 0');
    setAppliedCredit(0);
    saveAppliedCreditToStorage(userId, 0);
  };

  return {
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    applyCredit,
    resetAppliedCredit
  };
};
